import { z } from 'zod';
import { ASSET_STATUSES, DEPRECIATION_METHODS } from './assets.js';
import { ColumnFiltersParamSchema } from './columnFilters.js';

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const DayString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ يجب أن يكون بصيغة سنة-شهر-يوم.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const money = z
  .number()
  .min(0, 'القيمة لا يمكن أن تكون سالبة.')
  .max(99_999_999_999.99, 'القيمة أكبر من الحد المسموح.');

/* ==========================================================================
   التصنيفات
   ========================================================================== */

export const CreateAssetCategorySchema = z.object({
  name: z.string().trim().min(2, 'اسم التصنيف مطلوب.').max(120),
  method: z.enum(DEPRECIATION_METHODS).default('STRAIGHT_LINE'),
  rate: z.number().min(0).max(100).default(25),
  usefulLifeMonths: z.number().int().min(1).max(1200).default(48),
  assetAccountId: Cuid,
  accumulatedAccountId: Cuid,
  expenseAccountId: Cuid,
});

export type CreateAssetCategoryInput = z.infer<typeof CreateAssetCategorySchema>;

export const UpdateAssetCategorySchema = CreateAssetCategorySchema.partial().extend({
  isActive: z.boolean().optional(),
});

export type UpdateAssetCategoryInput = z.infer<typeof UpdateAssetCategorySchema>;

/* ==========================================================================
   الأصول
   ========================================================================== */

export const CreateAssetSchema = z
  .object({
    categoryId: Cuid,
    code: optionalText(40),
    name: z.string().trim().min(2, 'اسم الأصل مطلوب.').max(200),
    description: optionalText(1000),
    serialNumber: optionalText(120),
    location: optionalText(200),

    purchaseDate: DayString,
    purchaseCost: money.refine((v) => v > 0, 'تكلفة الاقتناء مطلوبة.'),
    salvageValue: money.default(0),

    /* تُنسخ من التصنيف إن لم تُذكر. */
    method: z.enum(DEPRECIATION_METHODS).optional(),
    rate: z.number().min(0).max(100).optional(),
    usefulLifeMonths: z.number().int().min(1).max(1200).optional(),

    custodianId: Cuid.nullable().optional(),
    supplierId: Cuid.nullable().optional(),
  })
  .refine((v) => v.salvageValue < v.purchaseCost, {
    message: 'قيمة الخردة يجب أن تقل عن تكلفة الاقتناء.',
    path: ['salvageValue'],
  });

export type CreateAssetInput = z.infer<typeof CreateAssetSchema>;

/**
 * التعديل لا يشمل التكلفة ولا تاريخ الشراء بعد بدء الإهلاك.
 *
 * تغييرهما يغيّر أقساطًا رُحِّلت إلى الدفتر، فتختلف الأرقام المحفوظة عمّا
 * يعيد النظام حسابه — والخادم هو من يفرض هذا لا المخطط.
 */
export const UpdateAssetSchema = z.object({
  name: z.string().trim().min(2).max(200).optional(),
  code: optionalText(40),
  description: optionalText(1000),
  serialNumber: optionalText(120),
  location: optionalText(200),
  purchaseCost: money.optional(),
  salvageValue: money.optional(),
  purchaseDate: DayString.optional(),
  method: z.enum(DEPRECIATION_METHODS).optional(),
  rate: z.number().min(0).max(100).optional(),
  usefulLifeMonths: z.number().int().min(1).max(1200).optional(),
  status: z.enum(['ACTIVE', 'UNDER_MAINTENANCE']).optional(),
  supplierId: Cuid.nullable().optional(),
});

export type UpdateAssetInput = z.infer<typeof UpdateAssetSchema>;

export const AssetListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(ASSET_STATUSES).optional(),
  categoryId: Cuid.optional(),
  custodianId: Cuid.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  /** مدى تاريخ الشراء — والطرف الأخير داخلٌ يومُه كلّه. */
  from: DayString.optional(),
  to: DayString.optional(),
  /** فرزٌ من رأس العمود — بلا قيمةٍ يبقى الترتيب الافتراضي. */
  sort: z.string().trim().max(40).optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
  /** شروط الأعمدة من زرّ «فلتر» — انظر `columnFilters.ts`. */
  f: ColumnFiltersParamSchema,
});

export type AssetListQuery = z.infer<typeof AssetListQuerySchema>;

/** ملخّص سجلّ الأصول — بمدى تاريخ الشراء نفسه الذي في القائمة. */
export const AssetSummaryQuerySchema = AssetListQuerySchema.pick({ from: true, to: true });

export type AssetSummaryQuery = z.infer<typeof AssetSummaryQuerySchema>;

/* ==========================================================================
   العهدة
   ========================================================================== */

export const AssignCustodySchema = z.object({
  /** فارغ يعني ردًّا إلى المخزن. */
  employeeId: Cuid.nullable(),
  note: optionalText(300),
});

export type AssignCustodyInput = z.infer<typeof AssignCustodySchema>;

/* ==========================================================================
   الاستبعاد
   ========================================================================== */

export const DisposeAssetSchema = z.object({
  date: DayString,
  /** ما قُبض من بيعه؛ صفر للتخريد. */
  proceeds: money.default(0),
  writeOff: z.boolean().default(false),
  note: optionalText(500),
});

export type DisposeAssetInput = z.infer<typeof DisposeAssetSchema>;

/* ==========================================================================
   الإهلاك
   ========================================================================== */

export const RunDepreciationSchema = z.object({
  /** شهر الإهلاك؛ أول يوم منه. */
  period: DayString,
});

export type RunDepreciationInput = z.infer<typeof RunDepreciationSchema>;

/* ==========================================================================
   الجرد
   ========================================================================== */

export const StartCountSchema = z.object({
  name: z.string().trim().min(2, 'اسم الجلسة مطلوب.').max(160),
  notes: optionalText(1000),
  categoryId: Cuid.optional(),
});

export type StartCountInput = z.infer<typeof StartCountSchema>;

export const CountLineSchema = z.object({
  assetId: Cuid,
  found: z.boolean(),
  foundCustodianId: Cuid.nullable().optional(),
  foundLocation: optionalText(200),
  note: optionalText(300),
});

export const SaveCountSchema = z.object({
  lines: z.array(CountLineSchema).min(1, 'لا صفوف للحفظ.').max(500),
});

export type SaveCountInput = z.infer<typeof SaveCountSchema>;

export const CloseCountSchema = z.object({
  /**
   * هل تُطبَّق نتيجة الجرد على السجلات؟
   *
   * الافتراضي لا: الجرد يكشف الفروق ولا يصححها من تلقاء نفسه. المفقود قد
   * يكون في سيارة موظف لا ضائعًا، وإعدامه تلقائيًا يمحو أصلًا موجودًا.
   */
  applyFindings: z.boolean().default(false),
  notes: optionalText(1000),
});

export type CloseCountInput = z.infer<typeof CloseCountSchema>;
